import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import utilStyles from "../../asset/styles/util/util.module.scss"
import styles from "../../asset/styles/transportation/dayTrip.module.scss"
import Grid from "@mui/material/Grid"
import Button from "component/button/button"
import Popup from "../../component/popup/popup"
import IconsColor from "../../util/iconsColor"
import PackageDetails from "./PackageDayTripSelection"
import PackagePricing from "./PackagePricing"
import {
    PackageDetailModel,
    getEmptyPackageDetail,
} from "./packagesDetailTypes"
import { agent } from "apiClient/api"
import { useSnackbar } from "../../context/snackbarContext"
import type { PackageItem } from "../transportation/package"
import type { DayTripItem } from "../transportation/DatTrip"

/** Shape of one item from GET /api/transportation/package-details response */
interface PackageDetailApiItem {
    package_id: string
    package_name: string
    flag_active: boolean
    package_day_trip?: Array<{
        package_day_trip_id: string
        package_id: string
        package_name: string
        day_trip_id: string
        day_trip_name: string
    }>
    package_trip_price?: Array<{
        trip_pricing_id: string
        package_id: string
        package_name: string
        seating_type_id: number
        seating_type_name: string
        price: number
        flag_active: boolean
    }>
}

function apiPackageDetailToModel(
    item: PackageDetailApiItem,
    dayTrips: DayTripItem[]
): PackageDetailModel {
    const dayTripMap = new Map(dayTrips.map((d) => [d.day_trip_id, d.day_trip_name ?? ""]))
    return {
        package_id: item.package_id,
        package_name: item.package_name,
        flag_active: item.flag_active,
        package_day_trip: (item.package_day_trip ?? []).map((t) => ({
            day_trip_id: t.day_trip_id,
            day_trip_name: t.day_trip_name || (dayTripMap.get(t.day_trip_id) ?? ""),
            package_day_trip_id: t.package_day_trip_id,
        })),
        package_trip_price: (item.package_trip_price ?? []).map((p) => ({
            seating_type_id: p.seating_type_id,
            seating_type_name: p.seating_type_name,
            price: p.price,
            trip_pricing_id: p.trip_pricing_id,
        })),
    }
}

export interface PackageDetailValidationErrors {
    package_name?: string
    trips?: string
    pricing?: string
}

function apiPackageToModel(item: PackageItem): PackageDetailModel {
    return {
        package_id: item.package_id,
        package_name: item.package_name,
        flag_active: item.flag_active,
        package_day_trip: (item.package_day_trip ?? []).map((t) => ({
            day_trip_id: t.day_trip_id,
            day_trip_name: t.day_trip_name,
            package_day_trip_id: t.package_day_trip_id,
        })),
        package_trip_price: (item.package_trip_price ?? []).map((p) => ({
            seating_type_id: p.seating_type_id,
            seating_type_name: p.seating_type_name,
            price: p.price,
            trip_pricing_id: p.trip_pricing_id,
        })),
    }
}

function validatePackageDetail(data: PackageDetailModel): PackageDetailValidationErrors {
    const errors: PackageDetailValidationErrors = {}
    if (!(data.package_name ?? "").trim()) {
        errors.package_name = "Package name is required"
    }
    const trips = data.package_day_trip ?? []
    if (trips.length === 0) {
        errors.trips = "At least one trip is required. You cannot delete all trips."
    } else {
        const hasEmptyTrip = trips.some(
            (t) => !(t.day_trip_id ?? "").trim()
        )
        if (hasEmptyTrip) {
            errors.trips = "Required"
        }
    }
    const prices = data.package_trip_price ?? []
    const hasInvalidPrice = prices.some(
        (p) =>
            p.price == null ||
            !Number.isFinite(p.price) ||
            p.price < 0 ||
            p.price === 0
    )
    if (prices.length > 0 && hasInvalidPrice) {
        errors.pricing = "All pricing columns are required"
    }
    return errors
}

function hasValidationErrors(errors: PackageDetailValidationErrors): boolean {
    return Object.keys(errors).length > 0
}

function modelToPutRequest(data: PackageDetailModel) {
    const packageId =
        !data.package_id || data.package_id === "new" ? null : data.package_id
    return {
        package_id: packageId,
        package_name: data.package_name.trim(),
        flag_active: data.flag_active,
        package_day_trip: (data.package_day_trip ?? [])
            .filter((t) => (t.day_trip_id ?? "").trim())
            .map((t) => ({ day_trip_id: t.day_trip_id })),
        package_trip_price: (data.package_trip_price ?? []).map((p) => ({
            trip_pricing_id: p.trip_pricing_id ?? null,
            seating_type_id: p.seating_type_id,
            price: p.price,
            flag_active: false,
        })),
    }
}

const PackagesDetail: React.FC = () => {
    const { package_id } = useParams<{ package_id: string }>()
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbar()
    const [data, setData] = useState<PackageDetailModel | null>(null)
    const [validationErrors, setValidationErrors] =
        useState<PackageDetailValidationErrors>({})
    const [dayTrips, setDayTrips] = useState<DayTripItem[]>([])
    const [inactiveErrorData, setInactiveErrorData] = useState<{ day_trip_name: string; message: string }[] | null>(null)

    useEffect(() => {
        if (package_id === "new" || !package_id) {
            setData(getEmptyPackageDetail())
            return
        }
        Promise.all([
            agent.PackageDetailAPI.get(package_id),
            agent.DayTripListAPI.get(),
        ])
            .then(([detailRes, dayTripRes]) => {
                const trips: DayTripItem[] =
                    dayTripRes?.status === true && Array.isArray(dayTripRes?.data)
                        ? dayTripRes.data
                        : []
                setDayTrips(trips)
                if (detailRes?.status === true && Array.isArray(detailRes?.data) && detailRes.data.length > 0) {
                    const detail = detailRes.data[0] as PackageDetailApiItem
                    setData(apiPackageDetailToModel(detail, trips))
                } else {
                    const empty = getEmptyPackageDetail()
                    setData({ ...empty, package_id })
                }
            })
            .catch((e) => {
                console.error("PackagesDetail fetch error", e)
                setData(getEmptyPackageDetail())
                showError("Failed to load package")
            })
    }, [package_id, showError])

    useEffect(() => {
        if ((package_id === "new" || !package_id) && !data) {
            setData(getEmptyPackageDetail())
        }
    }, [package_id, data])

    useEffect(() => {
        const fetchDayTrips = async () => {
            try {
                const res = await agent.DayTripListAPI.get()
                if (res?.status === true && Array.isArray(res?.data)) {
                    setDayTrips(res.data)
                }
            } catch (e) {
                console.error("PackagesDetail fetch day trips error", e)
            }
        }
        fetchDayTrips()
    }, [])

    const updateData = (updates: Partial<PackageDetailModel>) => {
        setData((prev) => (prev ? { ...prev, ...updates } : null))
    }

    const handleBack = () => {
        navigate("/transportation", { state: { tab: "packages" } })
    }

    const handleSave = async () => {
        if (!data) return
        const errors = validatePackageDetail(data)
        setValidationErrors(errors)
        if (hasValidationErrors(errors)) return
        const body = modelToPutRequest(data)
        try {
            const res = await agent.PackagePutAPI.put(body)
            if (res?.status === true && res?.data) {
                setData(apiPackageToModel(res.data[0] as PackageItem))
                setValidationErrors({})
                showSuccess(
                    body.package_id == null
                        ? "Package created successfully."
                        : "Package updated successfully."
                )
            } else if (res?.status === false && res?.error?.code === 9046 && Array.isArray(res?.data) && res.data.length > 0) {
                setInactiveErrorData(
                    res.data.map((item: { day_trip_name?: string; message?: string }) => ({
                        day_trip_name: item.day_trip_name ?? "",
                        message: item.message ?? "",
                    }))
                )
            } else {
                showError(res?.message ?? "Failed to save package.")
            }
        } catch (e) {
            console.error("Package save error", e)
            showError("Failed to save package.")
        }
    }

    if (!data) {
        return null
    }

    return (
        <div className={utilStyles.pageContainer}>
            <Popup
                isOpen={!!inactiveErrorData}
                onClose={() => setInactiveErrorData(null)}
                title="Cannot update Package"
                primaryButton={{ label: "OK", onClick: () => setInactiveErrorData(null) }}
                heightFiexd={true}
            >
                {inactiveErrorData && inactiveErrorData.length > 0 ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                        <p className={styles.confirmationText}>The following day trips are missing or inactive:</p>
                        <div className={styles.dayTripUsageWarning}>
                            <ul className={styles.dayTripUsageList}>
                                {inactiveErrorData.map((item, index) => (
                                    <li key={index}>
                                        <strong>{item.day_trip_name}</strong> — {item.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ) : null}
            </Popup>
            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 8 }}>
                    <h1 className={utilStyles.header}>
                        {package_id === "new"
                            ? "Add Package"
                            : "Package Detail"}
                    </h1>
                </Grid>
                <Grid
                    size={{ xs: 12, md: 4 }}
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                    }}
                >
                    <Button
                        type="outlined"
                        label="← Back"
                        style={{ marginRight: "16px" }}
                        onClick={handleBack}
                    />
                    <Button type="contained" label="Save" onClick={handleSave} />
                </Grid>
                <Grid size={{ xs: 4 }}>
                    <PackageDetails
                        data={data}
                        dayTrips={dayTrips}
                        onChange={updateData}
                        errors={{
                            package_name: validationErrors.package_name,
                            trips: validationErrors.trips,
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <PackagePricing
                        data={data}
                        onChange={updateData}
                        error={validationErrors.pricing}
                    />
                </Grid>
            </Grid>
        </div>
    )
}

export default PackagesDetail
